import {Injectable} from '@angular/core';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BimSection, BimSectionPlan} from '../bp-common/models/plan';
import {isPlanCalibrated} from './mm-bim-view/helpers/helpers';
import {Structure} from '@docma-mm-core/structure/structure';
import {filter, map, switchMap} from 'rxjs/operators';
import {MmPlansManagerService} from '../mm-faults/mm-plans-view/services/mm-plans-manager.service';
import {Plan} from '@docma-mm-core/plan/plan';
import {MmProjectService} from '../mm-project/mm-project.service';
import {Project} from '@docma-mm-core/project/project';
import {PersonalizationService} from '@docma-mm-core/personalization/personalization.service';
import {MmFaultsSplitService} from '../mm-faults/mm-faults-view/services/mm-faults-split.service';
import {BimModel, BimSectionApiService} from '@mydocma/co/src/lib/common';
import { Logger } from '@mydocma/common/src/lib/logger';


@UntilDestroy()
@Injectable()
export class MmBimService {

  mmPlanManager: MmPlansManagerService;

  public sectionsByPlanId: Map<string, BimSection>;
  public sectionPlansByPlanId: Map<string, BimSectionPlan>;
  private selectedBimModelSubject = new BehaviorSubject<BimModel>(null);
  public selectedBimModel$ = this.selectedBimModelSubject.asObservable();
  private sectionsSubject = new BehaviorSubject<BimSection[]>([]);
  public sections$ = this.sectionsSubject.asObservable();
  private sectionsPlansSubject = new BehaviorSubject<BimSectionPlan[]>(null);
  private plansSubject = new BehaviorSubject<Plan[]>([]);
  public bimPlans$ = this.plansSubject.asObservable();
  private project: Project;
  private selectedPlanSubject = new BehaviorSubject<Plan>(null);
  public selectedPlan$ = this.selectedPlanSubject.asObservable();
  private isBimViewOpen = false;

  public set IsBimViewOpen(value: boolean) {
    this.isBimViewOpen = value;
  }

  constructor(private projectService: MmProjectService,
              private sectionApiService: BimSectionApiService,
              private readonly mmPersonalizationService: PersonalizationService,
              readonly faultsSplitService: MmFaultsSplitService,
              private readonly logger: Logger) {

  }

  attachTo(targetToAttach: any) {
    if (targetToAttach instanceof MmPlansManagerService) {
      this.mmPlanManager = targetToAttach;
    }

    this.projectService.currentProject.pipe(untilDestroyed(this))
      .subscribe(async project => {
        this.reset();
        this.project = project;
      });

    this.sectionsPlansSubject.pipe(
      switchMap(bimPlans => this.mmPlanManager.plansByStructure$.pipe(map(mmPlans => ({bimPlans, mmPlans})))),
      untilDestroyed(this)
    ).subscribe(r => {
      r.bimPlans = r.bimPlans || [];
      const mmBimPlans = (r.mmPlans || []).filter(mmPlan => (this.bimPlans ?? []).find(bp => mmPlan.id.toString() === bp.planId));
      this.plansSubject.next(mmBimPlans);
    });


    this.plansSubject
      .pipe(untilDestroyed(this))
      .subscribe(plans => {
        const current = this.selectedPlan;
        if (!current) {
          return;
        }
        const fresh = (plans || []).find(p => p?.id === current.id);
        if (fresh && fresh !== current) {
          this.selectedPlanSubject.next(fresh);
        }
      });

    this.plansSubject
      .pipe(
        switchMap(plans => this.mmPlanManager.selectedPlan$.pipe(
          filter(sp => !sp || (sp?.id !== this.selectedPlan?.id && this.faultsSplitService.toggledRightViews.length > 0 && this.faultsSplitService.toggledRightViews[0]  == 'planView')),
          map(selectedPlan => ({plans, selectedPlan})))
        ),
        untilDestroyed(this)
      )
      .subscribe(r => {

        const plansCount = (r.plans || []).length;
        if (plansCount === 0) {
          this.selectPlan(null);
          return;
        } else if (plansCount === 1 && this.isBimViewOpen) {
          r.selectedPlan = r.plans[0];
        }

        if (r.selectedPlan) {
          // select plan from mm plan manager
          if ((this.plansSubject.value || []).find(p => p.id === r.selectedPlan.id)) {
            this.selectPlan(r.selectedPlan);
          } else {
            r.selectedPlan = null;
            this.selectPlan(null);
          }
        } else {
          this.selectPlan(null);
        }
      });
  }


  public async setSelectedBimModel(bimModel: BimModel) {

    if (!bimModel) {
      this.reset();
      return;
    }
    await this.setSections(bimModel);
    this.selectedBimModelSubject.next(bimModel);

  }


  get selectedPlan(): Plan {

    return this.selectedPlanSubject.value;
  }

  get sections(): BimSection[] {
    return this.sectionsSubject.value;
  }

  get bimPlans(): BimSectionPlan[] {
    return this.sectionsPlansSubject.value;
  }

  get defaultBimModel(): BimModel {
    return this.selectedBimModelSubject.value;
  }

  public selectPlan(plan: Plan) {
    if (!plan || this.plansSubject.value.find(p => p?.id === plan?.id)) {
      this.saveLastPlan(plan);
      this.selectedPlanSubject.next(plan);
      if (plan) {
        this.mmPlanManager.setSelectedPlan(plan);
      }
      return;
    }
    throw new Error(`Can't select plan with id ${plan?.id}`);
  }

  getSectionByPlanId(planId: string): BimSection {
    return this.sectionsByPlanId.get(planId);
  }

  getSectionPlan(planId: string): BimSectionPlan {
    return this.sectionPlansByPlanId.get(planId);
  }

  public resetState() {
    this.selectedBimModelSubject.next(null);
    this.sectionsSubject.next(null);
  }

  public reset(): void {
    this.selectedBimModelSubject.next(null);
    this.sectionsSubject.next([]);
    this.sectionsPlansSubject.next([]);
    this.sectionsByPlanId = new Map<string, BimSection>();
    this.sectionPlansByPlanId = new Map<string, BimSectionPlan>();

  }

  private async selectLastPlan() {
    // const lastBimPlanId = await this.mmPersonalizationService.getProjectValue(this.project?.MyDocmaUid, PersonalizationKeysEnum.LastBimPlanId);
    // if (lastBimPlanId) {
    //   const foundPlan = (this.plansSubject.value || []).find(x => x?.id === lastBimPlanId);
    //   if (foundPlan) {
    //     this.selectPlan(foundPlan);
    //   }
    // }
  }

  private async saveLastPlan(plan: Plan) {
    // if (plan?.id) {
    //   await this.mmPersonalizationService.setProjectValue(this.project?.MyDocmaUid, PersonalizationKeysEnum.LastBimPlanId, plan.id);
    // }
  }
  // todo sinnce out bimModel dont havve deafult property will commented  this methdo
  private async setSections(bimModel: BimModel): Promise<void> {

    try {
      const modelId = bimModel.id;
      if (!modelId) {
        return;
      }

      const sections = await firstValueFrom(this.sectionApiService.getBimSections(modelId.toString())) ;
      const bimPlans = sections.reduce(
        (arr, section) => {

          return arr.concat(section?.plans.filter(bimPlan => {
            if (isPlanCalibrated(bimPlan)) {
              this.sectionsByPlanId.set(bimPlan.planId, section);
              this.sectionPlansByPlanId.set(bimPlan.planId, bimPlan);
              return true;
            }
            return false;
          }) || []);
        }
        , []) as BimSectionPlan[];

      this.sectionsSubject.next(sections);
      this.sectionsPlansSubject.next(bimPlans);


    } catch (e) {
      this.logger.error(e, '', 'MmBimService');
      this.reset();
    }
  }



}


export function getChildBimPlansOfStructure(selectedStructure: Structure, planList: BimSectionPlan[]): BimSectionPlan[] {
  let structures = [];
  if (selectedStructure.getFlatSortedStructures) {
    structures = selectedStructure.getFlatSortedStructures();
  }
  let childPlans = [];
  structures.forEach(struct => {
    const plans = planList.filter(plan => plan.plan.structureId === struct.Id);
    childPlans = [...childPlans, ...plans];
  });

  return childPlans;
}
